import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { FormBuilder, FormGroup } from '@angular/forms';

import { Observable, Subscription, Subject, BehaviorSubject } from 'rxjs';
import {
  tap,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs/operators';

import {
  Student,
  LearningPathStudentSubscription,
  LearningPathStudentGeneral,
} from '../../models/interfaces';
import { LearnpathService } from '../../services/learnpath.service';

@Component({
  selector: 'app-learning-path-employee-dashboard',
  templateUrl: './learning-path-employee-dashboard.component.html',
  styleUrls: ['./learning-path-employee-dashboard.component.scss'],
})
export class LearningPathEmployeeDashboardComponent
  implements OnInit, OnDestroy {
  public learningPathId: number;

  public subscribed: LearningPathStudentGeneral[] = [];
  public notSubscribed: Student[] = [];
  public searchForm: FormGroup;

  private _routeSubscription: Subscription;
  private _subscriptions: Subscription[] = [];
  private _pageSize = 20;
  private _notSubscribedPage = 0;
  private _subscribedPage = 0;

  private _inputLearnpathId?: number;
  get inputLearnpathId(): number {
    return this._inputLearnpathId;
  }

  @Input() set inputLearnpathId(id: number) {
    if (id && id > 0) {
      this.learningPathId = +id;
      this._inputLearnpathId = +id;
      this.getNotSubscribedStudents();
      this.getSubscribedStudents();
    }
  }

  public constructor(
    private _learningPathService: LearnpathService,
    private _route: ActivatedRoute,
    private _formBuilder: FormBuilder
  ) {
    this.searchForm = this._formBuilder.group({
      search: '',
    });
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    window.parent.scrollTo(0, 0);
    window.top.scrollTo(0, 0);

    this._subscriptions.push(
      this.searchForm
        .get('search')
        .valueChanges.pipe(debounceTime(250), distinctUntilChanged())
        .subscribe(() => {
          this.reloadNotSubscribed();
        })
    );

    if (this.inputLearnpathId) {
      this.learningPathId = +this.inputLearnpathId;
      this.reloadNotSubscribed();
      this.reloadSubscribed();
    } else {
      this._routeSubscription = this._route.parent.params.subscribe(
        (params: Params) => {
          this.learningPathId = +params.id;
          this.reloadSubscribed();
          this.reloadNotSubscribed();
        }
      );
    }
  }

  public ngOnDestroy(): void {
    if (this._routeSubscription) {
      this._routeSubscription.unsubscribe();
    }

    for (const subscr of this._subscriptions) {
      subscr.unsubscribe();
    }
  }

  public endDrag(event: CdkDragDrop<Student>): void {
    if (this.learningPathId) {
      const subscription: LearningPathStudentSubscription = {
        learningPathId: this.inputLearnpathId || this.learningPathId,
        createStudent: true,
        learningPathStudentId: 0,
        studentId: event.item.data.studentId,
      };
      this._subscriptions.push(
        this._learningPathService
          .subscribeEmployee(subscription)
          .subscribe((lpStudent: LearningPathStudentGeneral) => {
            this.getNotSubscribedStudents();
            this.getSubscribedStudents();

            this.notSubscribed = this.notSubscribed.filter(
              (stud) => stud.studentId !== lpStudent.studentId
            );
          })
      );
    }
  }

  public reloadSubscribed(): void {
    this._subscribedPage = 0;
    this.subscribed = [];
    this.getSubscribedStudents();
  }

  public reloadNotSubscribed(): void {
    this._notSubscribedPage = 0;
    this.notSubscribed = [];
    this.getNotSubscribedStudents();
  }

  public onScrollNotSubscribed(): void {
    this._notSubscribedPage++;
    this.getNotSubscribedStudents();
  }

  public onScrollSubscribed(): void {
    this._subscribedPage++;
    this.getSubscribedStudents();
  }

  private getNotSubscribedStudents() {
    this._subscriptions.push(
      this._learningPathService
        .getUnsubscribedEmployees(
          this.inputLearnpathId || this.learningPathId,
          {
            search: this.searchForm.get('search').value,
            page: this._notSubscribedPage,
            pageSize: this._pageSize,
          },
          true
        )
        .subscribe((students: Student[]) => {
          this.notSubscribed.push(...students);

          this.notSubscribed = this.notSubscribed.filter(
            (value, index, self) =>
              self.map((x) => x.studentId).indexOf(value.studentId) === index
          );
        })
    );
  }

  private getSubscribedStudents() {
    this._subscriptions.push(
      this._learningPathService
        .getSubscribedEmployees(
          this.inputLearnpathId || this.learningPathId,
          {
            search: '',
            page: this._subscribedPage,
            pageSize: this._pageSize,
          },
          true
        )
        .subscribe((students: LearningPathStudentGeneral[]) => {
          if (this._subscribedPage === 0) {
            this.subscribed = [];
          }

          this.subscribed.push(...students);

          this.subscribed = this.subscribed.filter(
            (value, index, self) =>
              self.map((x) => x.studentId).indexOf(value.studentId) === index
          );
        })
    );
  }
}
